import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import './utils/axiosConfig'; // Import axios configuration for auto-logout
import SimplePaymentPortal from './components/SimplePaymentPortal';
import AdminDashboard from './components/AdminDashboard';
import AgentDashboard from './components/AgentDashboard';
import HCCAgentDashboard from './components/HCCAgentDashboard';
import HCCAdminDashboard from './components/HCCAdminDashboard';
// New comprehensive dashboard components
import ComprehensiveAdminDashboard from './components/ComprehensiveAdminDashboard';
import ComprehensiveAgentDashboard from './components/ComprehensiveAgentDashboard';
import EnhancedPaymentPortal from './components/EnhancedPaymentPortal';
import DeclinedPaymentForm from './components/DeclinedPaymentForm';
import ClientProfile from './components/ClientProfile';
import AdminUtilities from './components/AdminUtilities';

// Login component
function Login() {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userData = await login(credentials.username, credentials.password);

      // Force immediate redirect based on user role
      if (userData.role === 'admin') {
        window.location.href = '/admin';
      } else if (userData.role === 'agent') {
        window.location.href = '/payment';
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="login-card">
          <div className="text-center mb-8">
            <div className="paylow-logo">
              <span className="pay">Pay</span><span className="low">Low</span>
            </div>
            <div className="paylow-tagline">Insurance Portal</div>
            <p className="text-gray-600 mb-6">Secure Agent & Admin Access</p>
          </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="text"
                required
                className="form-input rounded-t-md"
                placeholder="Username"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              />
            </div>
            <div>
              <input
                type="password"
                required
                className="form-input rounded-b-md"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              />
            </div>
          </div>

          {error && (
            <div className="error-message text-center">{error}</div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading && <span className="loading-spinner"></span>}
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </div>
        </form>
        
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            PayLow Insurance Payment Portal • Secure & Encrypted
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}

// Protected route component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
        <span>Loading...</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

// Role-based redirect component
function RoleBasedRedirect() {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  if (user.role === 'agent') {
    return <Navigate to="/payment" replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Protected payment portal routes - Require agent login */}
            <Route 
              path="/payment" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <EnhancedPaymentPortal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment/:agentId" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <EnhancedPaymentPortal />
                </ProtectedRoute>
              } 
            />
            {/* Legacy simple portal for compatibility */}
            <Route 
              path="/simple-payment" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <SimplePaymentPortal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/simple-payment/:agentId" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <SimplePaymentPortal />
                </ProtectedRoute>
              } 
            />
            
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            {/* Public declined payment form - no authentication required */}
            <Route path="/declined-payment/:linkId" element={<DeclinedPaymentForm />} />
            
            {/* Protected routes - New comprehensive dashboards */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ComprehensiveAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/utilities" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminUtilities />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/agent" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <ComprehensiveAgentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/client/:clientId" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <ClientProfile />
                </ProtectedRoute>
              } 
            />
            
            {/* Legacy dashboard routes for compatibility */}
            <Route 
              path="/admin/legacy" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/hcc" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <HCCAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/agent/legacy" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/agent/hcc" 
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <HCCAgentDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Default redirects - role-based */}
            <Route path="/" element={
              <ProtectedRoute>
                <RoleBasedRedirect />
              </ProtectedRoute>
            } />
            <Route path="/unauthorized" element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-red-600 mb-4">Unauthorized</h1>
                  <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
              </div>
            } />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/payment" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;